import React from 'react'
import clsx from 'clsx'
import { useAuth0 } from '@auth0/auth0-react'
import { useTheme } from '@mui/material'
import { LogoutCurve } from 'iconsax-react'

import { getAccounts } from '../../lib/api'

import {
  LoginLogo,
  LoginTitle,
  LoginCopyright
} from '../../components/atoms/Login'
import Spacer from '../../components/atoms/Spacer'
import Button from '../../components/organisms/Login/Button'
import SelectEnvironment from '../../components/organisms/Login/SelectEnvironment'
import ErrorToast from '../../components/molecules/ErrorToast'

import { useBreakpoint } from '../../providers/BreakpointProvider'
import { useAgent } from '../../providers/AgentProvider'
import {
  getStoredAccount,
  setStoredAccount,
  useAccounts
} from '../../providers/AccountProvider'

import useStyles from './styles'

const LoginContainer = ({ classes, isNotSmall, error, children }) => {
  const theme = useTheme()

  return (
    <div className={classes.root}>
      <div className={classes.formBlock}>
        <LoginLogo />
        <LoginTitle title='AGENT PORTAL' size={theme.size.xl} />

        {children}

        {isNotSmall && <Spacer />}
        <LoginCopyright />
      </div>
      {error && <ErrorToast error={{ err: error }} />}
    </div>
  )
}

const Login = () => {
  const { setUserSession } = useAgent()
  const { callEnvironment } = useAccounts()
  const { isNotSmall } = useBreakpoint()
  const {
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    user,
    isLoading,
    isAuthenticated
  } = useAuth0()
  const [environment, setEnvironment] = React.useState(false)
  const [selectEnv, setSelectEnv] = React.useState(false)
  const [noAccounts, setNoAccounts] = React.useState(false)
  const [loadingToken, setLoadingToken] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [accounts, setAccounts] = React.useState([])

  const classes = useStyles({})

  // User chose one from environments selection
  const choseEnvironment = React.useCallback(val => {
    setStoredAccount(val.id)
  }, [])

  const login = async () => await loginWithRedirect()

  const onLoginSuccess = React.useCallback(async () => {
    try {
      setNoAccounts(false)
      setUserSession(
        { id: user.sub, name: '', phone: '', email: '' },
        await getAccessTokenSilently()
      )

      const accounts = await getAccounts()
      setLoadingToken(false)

      if (!accounts || accounts.length === 0) {
        //show no accounts
        setNoAccounts(true)
        return
      }
      const stored = getStoredAccount()
      // if user only has one environment or has a last_account stored
      if (accounts.length === 1 || stored) {
        let currentAccount = accounts[0].id
        if (stored) {
          // Check if allowed
          if (accounts.map(obj => obj.id).includes(stored)) {
            currentAccount = stored
          } else {
            // Go to environment select if the stored account is no longer valid
            setEnvironment(true)
            setAccounts(accounts)
            return
          }
        }
        setStoredAccount(currentAccount)
        setSelectEnv(true)
      } else {
        // More than one account and none saved so select screen is shown
        setEnvironment(true)
        setAccounts(accounts)
      }
    } catch (err) {
      setLoadingToken(false)
      console.error('error on login:', err)
      setError(err)
    }
  }, [getAccessTokenSilently, setUserSession, user])

  React.useEffect(() => {
    if (isLoading) return

    if (isAuthenticated) {
      setLoadingToken(true)
      onLoginSuccess()
    }
  }, [isAuthenticated, isLoading, onLoginSuccess])

  // Does environment call and redirect
  React.useEffect(() => {
    if (isLoading) return

    if (isAuthenticated && selectEnv) {
      const currentAccount = getStoredAccount()

      callEnvironment(currentAccount)
    }
  }, [isLoading, isAuthenticated, selectEnv, callEnvironment])

  if (isLoading || loadingToken) {
    return (
      <LoginContainer classes={classes} isNotSmall={isNotSmall} error={error}>
        <div className={clsx(classes.title, classes.loading)}>
          Loading your account...
        </div>
      </LoginContainer>
    )
  }

  if (error) {
    return (
      <LoginContainer classes={classes} isNotSmall={isNotSmall} error={error}>
        <div className={classes.title}>{error?.message}</div>
        <div style={{ width: '25%' }}>
          <Button
            onClick={() =>
              logout({
                logoutParams: { returnTo: `${window.location.origin}/login` }
              })
            }
          >
            SIGN OUT
            <LogoutCurve className={classes.icon} />
          </Button>
        </div>
      </LoginContainer>
    )
  }

  if (isAuthenticated && noAccounts) {
    return (
      <LoginContainer classes={classes} isNotSmall={isNotSmall} error={error}>
        <div className={classes.title}>
          You are not currently a member of any Sonar environments.
        </div>
        <div style={{ width: '25%' }}>
          <Button
            onClick={() =>
              logout({
                logoutParams: { returnTo: `${window.location.origin}/login` }
              })
            }
          >
            SIGN OUT
            <LogoutCurve className={classes.icon} />
          </Button>
        </div>
      </LoginContainer>
    )
  }

  if (isAuthenticated && environment) {
    return (
      <LoginContainer classes={classes} isNotSmall={isNotSmall} error={error}>
        {environment && (
          <SelectEnvironment
            accounts={accounts}
            environments={choseEnvironment}
            select={setSelectEnv}
          />
        )}
      </LoginContainer>
    )
  }

  if (!isLoading && !isAuthenticated) {
    login()
  }

  return <React.Fragment />
}

export default Login
